import * as React from 'react';
import type {
	FieldValues,
	UseControllerProps,
	ControllerRenderProps,
	FieldPath,
} from 'react-hook-form';
import { useController } from 'react-hook-form';

import { isErrorType, type CellError } from 'af-components/RHFTableCellComponents/error.types';

export type RenderMethodParams<
	TFieldValues extends FieldValues,
	TName extends FieldPath<TFieldValues>,
> = {
	field: ControllerRenderProps<TFieldValues, TName>;
	onFieldChange: (event: React.ChangeEvent) => void;
	onFieldBlur: () => void;
	error?: Nullable<CellError>;
};

export type RHFFieldCellWrapperProps<
	TFieldValues extends FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues extends FieldValues = TFieldValues
> = UseControllerProps<TFieldValues, TName, TTransformedValues> & {
	onChange?: (name: string) => void;
	onBlur: () => void;
	render: (params: RenderMethodParams<TFieldValues, TName>) => React.ReactElement;
};

/**
 * Field Wrapper around components
 * Offers controlled component by react hook form, tracks and updates RHF’s internal value store
 */
function RHFFieldCellWrapper<
	TFieldValues extends FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues extends FieldValues = TFieldValues
>(props: RHFFieldCellWrapperProps<TFieldValues, TName, TTransformedValues>) {
	const { name, control, defaultValue, rules, onChange, onBlur, render } = props;

	const { field, fieldState } = useController<TFieldValues, TName, TTransformedValues>({
		name,
		control,
		defaultValue,
		rules,
	});

	const onFieldChange = React.useCallback((event) => {
		field.onChange(event);
		onChange?.(name);
	}, [field, name, onChange]);

	const onFieldBlur = React.useCallback(() => {
		field.onBlur();
		onBlur();
	}, [field, onBlur]);

	const error: Nullable<CellError> = React.useMemo(() => {
		if (fieldState.error?.message) {
			return {
				message: fieldState.error.message,
				type: isErrorType(fieldState.error.type)
					? fieldState.error.type
					: 'other',
			};
		} else {
			return null;
		}
	}, [fieldState]);

	return render({
		field,
		error,
		onFieldChange,
		onFieldBlur,
	});
}

export default React.memo(RHFFieldCellWrapper) as typeof RHFFieldCellWrapper;
